import type { MatchState } from '../simulation/State.js';
import { TICK_MS } from '../simulation/State.js';
import type { Building, MiniTurret } from '../simulation/entities/Building.js';
import type { ControlNode } from '../simulation/entities/ControlNode.js';
import type { EntityId, PlayerId } from '../simulation/ids.js';

import type { MatchLifecycleEvent } from './MatchHost.js';

// V1.5+ Slice 3 — defender-side alert events.
//
// Pure function over two consecutive `MatchState` snapshots. Returns the
// list of `critical_flip` lifecycle events that should fire this tick.
// Both `LocalMatchHost` and `ColyseusMatchHost` invoke this from their
// per-tick pump and broadcast each event through the existing lifecycle
// channel — there's no transport-specific code here.
//
// Edge semantics (Rule 12 — written where the rule lives):
//
//   - 'link_cut'        — rising edge on `node.linkCutUntilTick` crossing
//                         `state.tick` (prev not in cut window, next in
//                         cut window). Skipped for neutral relays —
//                         victim must be a player. The `link_cut`
//                         variant takes priority over `relay_isolated`
//                         when a cut also breaks the chain.
//   - 'relay_isolated'  — rising edge on `node.isConnected` flipping
//                         true → false WITHOUT a cut on this node
//                         (chain broken upstream). Owned relays only.
//   - 'mini_turret_dark'— rising edge on `b.isOnline` flipping
//                         true → false for a `mini_turret`. Skipped on
//                         freshly-deployed turrets (they pass through
//                         AbilitySystem's one-tick `isOnline: true`
//                         grace before ForwardTurretNetworkSystem
//                         reconciles them).
//   - 'reconnected'     — falling edge on the "isolated" or "cut" state
//                         for an owned relay. Fires when:
//                           · cut window expires (linkCutUntilTick
//                             passes state.tick), OR
//                           · upstream link recaptures and the BFS
//                             marks the node connected again.
//                         One event per node per recovery edge.
//   - 'relay_jammed'    — V1.5+ Slice 5. Rising edge on
//                         `node.jammedUntilTick` crossing `state.tick`.
//                         Skipped for neutral relays. Independent of
//                         `link_cut` / `relay_isolated` (the chain
//                         doesn't break under a jam, so neither of
//                         those events fires concurrently). No
//                         falling-edge event — the banner countdown
//                         handles dismissal.
//   - 'uplink_disrupted'— V1.5+ Slice 6. Rising edge on
//                         `node.disruptedUntilTick` crossing
//                         `state.tick`. Owned relays only — like jam,
//                         a disruption on a neutral relay has no
//                         defender to alert. Independent of all the
//                         above (the chain stays intact, vision is
//                         unaffected, turrets stay online), so it
//                         fires alongside any concurrent flip event.
//                         No falling-edge event — banner countdown
//                         handles dismissal, parity with relay_jammed.
//   - 'relay_fortified' — V1.5+ Slice 7. Rising edge on
//                         `node.fortifiedUntilTick` crossing
//                         `state.tick`. UNLIKE the disruption events
//                         (link_cut / relay_jammed / uplink_disrupted)
//                         which fire for the relay's OWNER as the
//                         victim, fortify is a "good news" pulse for
//                         the FORTIFIER — `victimPlayerId` carries
//                         `fortifiedByPlayerId` (overloaded as
//                         "audience" for the success-banner variant).
//                         The banner reads it and renders a green
//                         "RELAY FORTIFIED — Xs" message instead of a
//                         red/orange/yellow disruption variant. No
//                         falling-edge event — countdown handles
//                         dismissal.
//
// Determinism: pure function of state + the wall-clock argument used
// only for `closesAtMs` derivation. No RNG (Rule 8). No allocation
// beyond the returned event list and a small per-call working scratch.
//
// Note on `prev === null` (the very first tick after `start()`): every
// rising-edge guard short-circuits to `false`, so no events fire. This
// avoids spurious "reconnected" pulses on match boot.
export function diffCriticalFlips(
  prev: MatchState | null,
  next: MatchState,
  nowMs: number,
): MatchLifecycleEvent[] {
  if (prev === null) return [];
  const events: MatchLifecycleEvent[] = [];
  diffNodeFlips(prev, next, nowMs, events);
  diffMiniTurretFlips(prev, next, events);
  return events;
}

function diffNodeFlips(
  prev: MatchState,
  next: MatchState,
  nowMs: number,
  out: MatchLifecycleEvent[],
): void {
  for (const idStr of Object.keys(next.controlNodes)) {
    const id = idStr as unknown as EntityId;
    const after = next.controlNodes[id];
    const before = prev.controlNodes[id];
    if (after === undefined) continue;
    if (before === undefined) continue;

    const wasCut = isCut(before, prev.tick);
    const isCutNow = isCut(after, next.tick);
    const wasJammed = isJammed(before, prev.tick);
    const isJammedNow = isJammed(after, next.tick);
    const wasDisrupted = isDisrupted(before, prev.tick);
    const isDisruptedNow = isDisrupted(after, next.tick);
    const wasFortified = isFortified(before, prev.tick);
    const isFortifiedNow = isFortified(after, next.tick);

    // 1. link_cut — rising edge into a cut window. Only emit when the
    //    target relay is owned by a player; cuts on a neutral relay
    //    have no defender to alert.
    if (!wasCut && isCutNow && after.ownerId !== null) {
      out.push({
        kind: 'critical_flip',
        reason: 'link_cut',
        victimPlayerId: after.ownerId,
        entityId: after.id,
        closesAtMs: nowMs + remainingCutMs(after, next.tick),
      });
      continue; // Skip the relay_isolated check — link_cut takes priority.
    }

    // 1b. relay_jammed — V1.5+ Slice 5 rising edge into a jam window.
    //     Owned relays only. A jam doesn't break the chain, so it
    //     doesn't suppress relay_isolated / reconnected the way a cut
    //     does — we only need to skip those checks if we ourselves
    //     fired (which we won't, since the underlying isConnected
    //     flag is unaffected).
    if (!wasJammed && isJammedNow && after.ownerId !== null) {
      out.push({
        kind: 'critical_flip',
        reason: 'relay_jammed',
        victimPlayerId: after.ownerId,
        entityId: after.id,
        closesAtMs: nowMs + remainingJamMs(after, next.tick),
      });
      // Don't `continue` — the same tick's chain-break / reconnected
      // checks below are still valid (a jam and an upstream capture can
      // race; both deserve a banner). This is the differentiator vs.
      // link_cut, which DOES suppress relay_isolated because a cut node
      // is also forced isConnected=false.
    }

    // 1c. uplink_disrupted — V1.5+ Slice 6 rising edge into a disruption
    //     window. Owned relays only. Same don't-continue semantics as
    //     jam: disruption is orthogonal to chain state (the BFS doesn't
    //     read disruptedUntilTick), so a concurrent capture / chain
    //     break still routes through the relay_isolated / reconnected
    //     paths.
    if (!wasDisrupted && isDisruptedNow && after.ownerId !== null) {
      out.push({
        kind: 'critical_flip',
        reason: 'uplink_disrupted',
        victimPlayerId: after.ownerId,
        entityId: after.id,
        closesAtMs: nowMs + remainingDisruptMs(after, next.tick),
      });
    }

    // 1d. relay_fortified — V1.5+ Slice 7 rising edge into a fortify
    //     window. The audience is the FORTIFIER (a "good news" pulse),
    //     not the relay's owner-as-victim. Self-fortifies on owned
    //     relays are the common case; cross-fortifies on enemy/neutral
    //     relays still fire (the immunity property is a legitimate
    //     deny-incoming-cut play). Independent of all chain state, so
    //     concurrent flip events still route through their own paths.
    if (!wasFortified && isFortifiedNow && after.fortifiedByPlayerId !== null) {
      out.push({
        kind: 'critical_flip',
        reason: 'relay_fortified',
        victimPlayerId: after.fortifiedByPlayerId,
        entityId: after.id,
        closesAtMs: nowMs + remainingFortifyMs(after, next.tick),
      });
    }

    // 2. relay_isolated — chain break NOT caused by a cut on this node.
    //    Only owned relays trigger; flips on neutral relays have no
    //    defender. Cuts already handled above. When ownership CHANGES
    //    on the same tick, the event is a capture (not a "relay
    //    isolated" event) — the new owner gaining-then-losing
    //    connectivity is a single moment of "flipping the relay" and
    //    routes through the existing capture-complete SFX path. Same-
    //    owner guard pins this case.
    const wasConnected = isOwnedAndConnected(before);
    const isConnectedNow = isOwnedAndConnected(after);
    if (
      wasConnected &&
      !isConnectedNow &&
      !isCutNow &&
      after.ownerId !== null &&
      before.ownerId === after.ownerId
    ) {
      out.push({
        kind: 'critical_flip',
        reason: 'relay_isolated',
        victimPlayerId: after.ownerId,
        entityId: after.id,
        closesAtMs: null,
      });
      continue;
    }

    // 3. reconnected — falling edge of either cut or isolation back to
    //    connected. The relay's owner must be the same on both sides
    //    (a flip-and-recapture in the same tick is rare and reads as a
    //    capture event, not a "good news" event for the new owner).
    if (
      !wasConnected &&
      isConnectedNow &&
      before.ownerId === after.ownerId &&
      after.ownerId !== null
    ) {
      out.push({
        kind: 'critical_flip',
        reason: 'reconnected',
        victimPlayerId: after.ownerId,
        entityId: after.id,
        closesAtMs: null,
      });
    }
  }
}

function diffMiniTurretFlips(
  prev: MatchState,
  next: MatchState,
  out: MatchLifecycleEvent[],
): void {
  for (const idStr of Object.keys(next.buildings)) {
    const id = idStr as unknown as EntityId;
    const after: Building | undefined = next.buildings[id];
    if (after === undefined) continue;
    if (after.kind !== 'mini_turret') continue;
    const before: Building | undefined = prev.buildings[id];
    if (before === undefined) continue;
    // The freshly-deployed turret pre-existed in `prev` only after its
    // first tick of life, where AbilitySystem set it `isOnline: true`
    // before ForwardTurretNetworkSystem reconciled. Rising-edge into
    // offline therefore means a real network event — not the deploy-
    // grace flicker.
    if (before.kind !== 'mini_turret') continue;
    if (before.isOnline === after.isOnline) continue;
    if (after.isOnline) continue; // 'reconnected' for turrets is implicit in the relay's reconnect event.
    out.push({
      kind: 'critical_flip',
      reason: 'mini_turret_dark',
      victimPlayerId: turretOwner(after),
      entityId: after.id,
      closesAtMs: null,
    });
  }
}

function isCut(node: ControlNode, tick: number): boolean {
  return tick < node.linkCutUntilTick;
}

function isJammed(node: ControlNode, tick: number): boolean {
  return tick < node.jammedUntilTick;
}

function isDisrupted(node: ControlNode, tick: number): boolean {
  return tick < node.disruptedUntilTick;
}

function isFortified(node: ControlNode, tick: number): boolean {
  return tick < node.fortifiedUntilTick;
}

function isOwnedAndConnected(node: ControlNode): boolean {
  return node.ownerId !== null && node.isConnected;
}

function turretOwner(b: MiniTurret): PlayerId {
  return b.ownerId;
}

// `remainingCutMs` converts the sim-tick deadline back to wall-clock ms
// so the banner can render a wall-clock countdown without the client
// having to read sim state. Uses TICK_MS (50 ms) — close enough to real
// time for a banner countdown, and stable across the V2 wire format
// (the client never sees the sim's accumulator).
function remainingCutMs(node: ControlNode, tick: number): number {
  const remainingTicks = Math.max(0, node.linkCutUntilTick - tick);
  return remainingTicks * TICK_MS;
}

function remainingJamMs(node: ControlNode, tick: number): number {
  const remainingTicks = Math.max(0, node.jammedUntilTick - tick);
  return remainingTicks * TICK_MS;
}

function remainingDisruptMs(node: ControlNode, tick: number): number {
  const remainingTicks = Math.max(0, node.disruptedUntilTick - tick);
  return remainingTicks * TICK_MS;
}

function remainingFortifyMs(node: ControlNode, tick: number): number {
  const remainingTicks = Math.max(0, node.fortifiedUntilTick - tick);
  return remainingTicks * TICK_MS;
}
